/**
 * @file: A thin vertical ribbon in the grid that shows what reaches the screen.
 *
 * The strip below the grid shows the same result on the horizontal axis, which
 * is easy to read for a whole day. In the grid, time runs down the page. An
 * operator who compares a bar with the result must therefore change between two
 * axes. This ribbon puts the result on the axis of the grid, at the left edge
 * of each day column. A run and its result are then side by side. Both surfaces
 * stay. One is for the day, and the other is for the moment.
 *
 * Each day column holds its own lane. The lane is the last child of the column
 * frame of FullCalendar, so it scrolls and clips with the grid. A run has a top
 * relative to that frame. A scroll moves the frame and the rows together, so a
 * scroll does not change the run. A zoom step changes the row heights, and the
 * zoom code then asks for a redraw.
 *
 * The lane class matches no query of the zoom code or the measuring code. The
 * lane takes no pointer events, so a gesture reaches the column below it. The
 * LESS file holds the position and the width of the lane.
 *
 * Use a div for every element. A span is inline and has no box, so the browser
 * ignores a measured height on a span.
 */

import { sameIntervalId, inspectedIntervalId, MIN_VISIBLE_RUN_PX } from '../calendar-intervals'
import { readSlotBoxes, yAtTime } from '../grid-geometry'

const LANE_CLASS = 'on-screen-ribbon'

/**
 * The runs of one day column, in the frame of that column.
 *
 * The code does not draw a stretch that has no winner. A stretch has no
 * winner when no interval covers it, or when two intervals have the same
 * highest priority. The grid is then visible. A gap is a normal state and not
 * a fault. A mark on each gap would cover each hour with no interval.
 *
 * @param {Array} segments - The pieces of the day, from the week snapshot
 * @param {Object} column - { date, slots, origin, selectedId }. The slots and
 *   the origin use the viewport frame. The origin is the top of the column frame.
 * @returns {Array} [{ top, height, colour, isSelected }], in pixels from the origin
 */
export function ribbonRuns (segments, column) {
  return (segments || [])
    .filter(segment => segment.winner)
    .map(segment => runOf(segment, column))
    .filter(Boolean)
}

/** The stretch of one winner, placed against the measured rows. */
function runOf (segment, { date, slots, origin, selectedId }) {
  const from = yAtTime(slots, date, segment.start)
  const to = yAtTime(slots, date, segment.end)

  if (from === null || to === null || to <= from) return null

  // A short run gets the minimum height. The code does not remove it. A gap
  // on this surface means that no interval is on screen. Without the run, the
  // gap shows a wrong result. At the widest zoom, two short runs side by side
  // can overlap by one pixel. The operator then sees that an interval is on
  // screen, but not its exact start and end. That loss is acceptable. A wrong
  // gap is not acceptable.
  return {
    top: from - origin,
    height: Math.max(to - from, MIN_VISIBLE_RUN_PX),
    colour: segment.winner.colour || null,
    isSelected: sameIntervalId(segment.winner.id, selectedId)
  }
}

/** Give each day column with its frame. The time axis is not a day. */
function readColumnFrames (calendarEl) {
  return Array.from(calendarEl.querySelectorAll('.fc-timegrid-col[data-date]'))
    .map(column => ({
      date: column.getAttribute('data-date'),
      frame: column.querySelector('.fc-timegrid-col-frame')
    }))
    .filter(column => column.frame)
}

function runElement (run) {
  const element = document.createElement('div')
  element.className = run.isSelected ? 'on-screen-ribbon-run is-selected' : 'on-screen-ribbon-run'
  element.style.top = `${run.top.toFixed(1)}px`
  element.style.height = `${run.height.toFixed(1)}px`
  // Set the colour as a property, and do not put it into markup. A playlist
  // colour is stored data, and the CSS parser removes a value that it cannot
  // read.
  element.style.background = run.colour || 'var(--jumbotron-primary)'

  return element
}

function laneElement (runs, hasSelection) {
  const lane = document.createElement('div')
  lane.className = LANE_CLASS
  lane.classList.toggle('has-selection', hasSelection)
  // The lane adds nothing that a screen reader cannot get from the bars.
  lane.setAttribute('aria-hidden', 'true')
  runs.forEach(run => lane.appendChild(runElement(run)))

  return lane
}

class OnScreenRibbon {
  constructor (controller) {
    this.controller = controller
  }

  /**
   * Replace the lanes of all day columns.
   *
   * The code reads all boxes first and writes the lanes after that, so the
   * browser does one layout and not one for each column.
   */
  render () {
    const calendar = this.controller.calendarManager && this.controller.calendarManager.calendar

    if (!calendar || !calendar.el) return

    const lanes = this.lanesOf(calendar.el)

    calendar.el.querySelectorAll(`.${LANE_CLASS}`).forEach(lane => lane.remove())
    lanes.forEach(({ frame, runs, hasSelection }) => frame.appendChild(laneElement(runs, hasSelection)))
  }

  /** Read the runs of each day column. A day with no winner gets no lane. */
  lanesOf (calendarEl) {
    const slots = readSlotBoxes(calendarEl)

    if (slots.length === 0) return []

    // A run that a stored, inspected interval wins gets the selection mark of
    // the grid. The code reads the selection during the render, so the mark
    // stays correct after a zoom and a refetch, with no extra state.
    const selectedId = inspectedIntervalId(this.controller)
    const week = this.controller.calendarManager.week()

    return readColumnFrames(calendarEl)
      .map(({ date, frame }) => ({
        frame,
        hasSelection: selectedId !== null,
        runs: ribbonRuns(week.segmentsOn(date), {
          date,
          slots,
          origin: frame.getBoundingClientRect().top,
          selectedId
        })
      }))
      .filter(lane => lane.runs.length > 0)
  }
}

export { OnScreenRibbon }
